import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  AlertTitle,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  CircularProgress,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material'
import {
  CheckCircle as SuccessIcon,
  CheckCircle,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CloudUpload as DeployIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as RecommendIcon,
  Close,
} from '@mui/icons-material'
import { 
  startDeployment, 
  getDeploymentStatus,
  verifyRequiredGroups,
  createLicenseGroupsPass1Async,
  getSubscriptionInfo,
  getCertificateStatus,
  getCustomer,
  verifyTenantAccess,
  getRecommendedScenarios,
  getBranding,
  getBrandingIcons,
  getEffectivePolicyPrefix,
} from '../services/api'
import type { DeploymentRequest, DeploymentResult, Deployment } from '../services/api'
import { getOrgBrandingBoxStyles, getSuccessBoxStyles } from '../utils/colorUtils'
import CertificateWizard from './CertificateWizard'
import GroupCreationProgressModal from './GroupCreationProgressModal'
import BrandingAssetStatusPanel from './BrandingAssetStatusPanel'

interface DeploymentWizardProps {
  tenantId?: string
  mspTenantId?: string
}

const standardLicenseScenarios = [
  {
    id: 'M365 E5',
    name: 'Microsoft 365 E5',
    description: 'Complete security, compliance, and productivity features',
    policies: 512,
    cost: 16000.00,
    recommended: false
  },
  {
    id: 'M365 Business Premium',
    name: 'Microsoft 365 Business Premium',
    description: 'Small business security and productivity',
    policies: 198,
    cost: 15000.00,
    recommended: false
  },
  {
    id: 'M365 E3',
    name: 'Microsoft 365 E3',
    description: 'Enterprise productivity and basic security',
    policies: 156,
    cost: 15000.00,
    recommended: false
  },
  {
    id: 'Exchange Online',
    name: 'Exchange Online Only',
    description: 'Email and calendar configuration without device management',
    policies: 89,
    cost: 10000.00,
    recommended: false
  }
]

const advancedLicenseScenarios = [
  {
    id: 'Intune + Entra P2',
    name: 'Intune + Entra ID P2',
    description: 'Device management and advanced identity protection',
    policies: 387,
    cost: 15500.00,
    recommended: false
  },
  {
    id: 'EDR + Intune',
    name: 'EDR + Intune',
    description: 'Advanced endpoint detection and response with device management',
    policies: 450,
    cost: 15000.00,
    recommended: false
  },
  {
    id: 'D4BIntune',
    name: 'Defender for Business + Intune',
    description: 'SMB security bundle with threat protection and device management',
    policies: 237,
    cost: 14500.00,
    recommended: false
  },
  {
    id: 'D4E1 + Intune',
    name: 'Defender for Endpoint P1 + Intune',
    description: 'Prevention-focused endpoint security with device management',
    policies: 320,
    cost: 14500.00,
    recommended: false
  },
  {
    id: 'Entra P2',
    name: 'Entra ID P2',
    description: 'Advanced identity protection with risk-based policies',
    policies: 185,
    cost: 14000.00,
    recommended: false
  },
  {
    id: 'Entra P1',
    name: 'Entra ID P1 Only',
    description: 'Identity protection and conditional access without device management',
    policies: 125,
    cost: 6000.00,
    recommended: false
  },
  {
    id: 'Intune + Entra P1',
    name: 'Intune + Entra ID P1',
    description: 'Basic device management and identity features',
    policies: 245,
    cost: 11000.00,
    recommended: false
  }
]

const steps = ['Select License', 'Setup Groups', 'Review Policies', 'Deploy', 'Results']

const deviceTypeOptions = [
  { key: 'Android', description: 'Android device policies' },
  { key: 'iPhone', description: 'iPhone device policies' },
  { key: 'iPad', description: 'iPad device policies' },
  { key: 'macOS', description: 'macOS device policies' },
  { key: 'Linux', description: 'Linux device policies' },
  { key: 'Windows', description: 'Windows device policies' }
]

export default function DeploymentWizard({ tenantId, mspTenantId }: DeploymentWizardProps) {
  const [searchParams] = useSearchParams()
  const retryFailedDeploymentId = searchParams.get('retryFailedDeploymentId')
  const isRetryMode = !!retryFailedDeploymentId
  
  const [activeStep, setActiveStep] = useState(isRetryMode ? 1 : 0)
  const [selectedScenario, setSelectedScenario] = useState<string>('')
  const retryingDeploymentId = isRetryMode && retryFailedDeploymentId ? parseInt(retryFailedDeploymentId, 10) : null
  
  const [enablePolicies, setEnablePolicies] = useState(false)
  const [deviceTypeSelections, setDeviceTypeSelections] = useState<Record<string, boolean>>({
    Android: false,
    iPhone: false,
    iPad: false,
    macOS: false,
    Linux: false,
    Windows: false
  })
  
  // Branding sub-options
  const [deployBrandingCustomization, setDeployBrandingCustomization] = useState(false)
  const [deployBrandingLogos, setDeployBrandingLogos] = useState(false)
  const [hasBrandingConfig, setHasBrandingConfig] = useState(false)
  const [hasBrandingIcons, setHasBrandingIcons] = useState(false)
  const [brandingIconCount, setBrandingIconCount] = useState(0)
  
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [recommendedScenarios, setRecommendedScenarios] = useState<{ recommended: string; viableScenarios: string[] } | null>(null)
  const [isRecommending, setIsRecommending] = useState(false)
  const [showCertificateDialog, setShowCertificateDialog] = useState(false)
  const [showCertificateWizard, setShowCertificateWizard] = useState(false)
  const [certificateStatus, setCertificateStatus] = useState<any>(null)
  const [certificateValid, setCertificateValid] = useState(false)
  const [checkingCertificate, setCheckingCertificate] = useState(true)
  const [verifyingAccess, setVerifyingAccess] = useState(false)
  const [groupsVerified, setGroupsVerified] = useState(false)
  const [verifyingGroups, setVerifyingGroups] = useState(false)
  const [creatingGroups, setCreatingGroups] = useState(false)
  const [groupCreationSessionId, setGroupCreationSessionId] = useState<string | null>(null)
  const [showGroupProgressModal, setShowGroupProgressModal] = useState(false)
  const [groupsStatus, setGroupsStatus] = useState<{allExist: boolean, missingCount: number, missingGroups: string[]}>({
    allExist: false,
    missingCount: 0,
    missingGroups: []
  })
  const [deploying, setDeploying] = useState(false)
  const [deploymentResult, setDeploymentResult] = useState<DeploymentResult | null>(null)
  const [deploymentStatus, setDeploymentStatus] = useState<Deployment | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [licenseAccess, setLicenseAccess] = useState({
    canDeployPolicies: false,
    canCreateGroups: false,
    canDeployBrandingOnly: false,
    requiresEnterpriseClientLicense: false,
    hasValidEnterpriseClientLicense: true,
    portalLockReason: null as string | null,
  })
  const [pollingInterval, setPollingInterval] = useState<number | null>(null)
  const [showSkippedDetails, setShowSkippedDetails] = useState(false)
  const [skippedDialogOpen, setSkippedDialogOpen] = useState(false)
  const [failedDialogOpen, setFailedDialogOpen] = useState(false)
  const [deployingBrandingOnly, setDeployingBrandingOnly] = useState(false)

  // MSP policy prefix for this deployment context
  const [effectivePolicyPrefix, setEffectivePolicyPrefix] = useState<string | null | undefined>(undefined)

  // Get color styles for boxes based on branding
  const headerBackgroundColor = localStorage.getItem('headerBackgroundColor') || '#0078d4'
  const boxStyles = getOrgBrandingBoxStyles(headerBackgroundColor)
  const successBoxStyles = getSuccessBoxStyles()
  
  // Debug: Log the colors being used
  useEffect(() => {
    console.log('🎨 DeploymentWizard - headerBackgroundColor:', headerBackgroundColor)
    console.log('🎨 DeploymentWizard - boxStyles:', boxStyles)
    console.log('🎨 DeploymentWizard - successBoxStyles:', successBoxStyles)
  }, [headerBackgroundColor, boxStyles, successBoxStyles])

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [pollingInterval])

  // Check certificate status on component mount
  useEffect(() => {
    if (tenantId) {
      checkCertificateOnLoad()
      checkBrandingConfigured()
      loadSubscriptionAccess()
    }
  }, [tenantId])

  // Resolve effective MSP policy prefix when both IDs are present
  useEffect(() => {
    if (!tenantId || !mspTenantId) {
      setEffectivePolicyPrefix(null)
      return
    }
    getEffectivePolicyPrefix(tenantId, mspTenantId)
      .then((result) => setEffectivePolicyPrefix(result.effectivePolicyPrefix ?? null))
      .catch(() => setEffectivePolicyPrefix(null))
  }, [tenantId, mspTenantId])

  // Auto-analyze licenses when certificate is valid
  useEffect(() => {
    if (tenantId && certificateValid && !checkingCertificate && !recommendedScenarios && !isRecommending && licenseAccess.canDeployPolicies) {
      handleRecommendConfiguration()
    }
  }, [tenantId, certificateValid, checkingCertificate, recommendedScenarios, isRecommending, licenseAccess.canDeployPolicies])

  const loadSubscriptionAccess = async () => {
    if (!tenantId) return

    try {
      const subscription = await getSubscriptionInfo(tenantId)
      const nextAccess = {
        canDeployPolicies: subscription.CanDeployPolicies ?? false,
        canCreateGroups: subscription.CanCreateGroups ?? false,
        canDeployBrandingOnly: subscription.CanDeployBrandingOnly ?? false,
        requiresEnterpriseClientLicense: subscription.RequiresEnterpriseClientLicense ?? false,
        hasValidEnterpriseClientLicense: subscription.HasValidEnterpriseClientLicense ?? true,
        portalLockReason: subscription.PortalLockReason ?? null,
      }
      setLicenseAccess(nextAccess)

      if (nextAccess.canDeployPolicies) {
        setEnablePolicies(true)
        setDeviceTypeSelections({
          Android: true,
          iPhone: true,
          iPad: true,
          macOS: true,
          Linux: true,
          Windows: true
        })
      } else {
        setEnablePolicies(false)
        setSelectedScenario('')
        setDeviceTypeSelections({
          Android: false,
          iPhone: false,
          iPad: false,
          macOS: false,
          Linux: false,
          Windows: false
        })
      }
    } catch (err) {
      console.error('Failed to load subscription access state:', err)
      setLicenseAccess({
        canDeployPolicies: false,
        canCreateGroups: false,
        canDeployBrandingOnly: false,
        requiresEnterpriseClientLicense: false,
        hasValidEnterpriseClientLicense: false,
        portalLockReason: 'Unable to verify deployment access. Please refresh and try again.',
      })
      setEnablePolicies(false)
      setSelectedScenario('')
      setDeviceTypeSelections({
        Android: false,
        iPhone: false,
        iPad: false,
        macOS: false,
        Linux: false,
        Windows: false
      })
    }
  }

  const checkCertificateOnLoad = async () => {
    if (!tenantId) return

    try {
      setCheckingCertificate(true)
      const certStatus = await getCertificateStatus(tenantId)
      console.log('🔍 Certificate status on load:', certStatus)
      setCertificateValid(certStatus.hasValidCertificate)
      console.log('🔍 Setting certificateValid to:', certStatus.hasValidCertificate)
    } catch (err: any) {
      console.error('Failed to check certificate status:', err)
      setCertificateValid(false)
    } finally {
      setCheckingCertificate(false)
    }
  }

  // Ensure certificate is present and functional before any deployment path
  const ensureCertificateConfigured = async (): Promise<boolean> => {
    if (!tenantId) return false

    try {
      setCheckingCertificate(true)
      const certStatus = await getCertificateStatus(tenantId)
      setCertificateValid(certStatus.hasValidCertificate)

      if (!certStatus.hasValidCertificate) {
        setError(certStatus.certificateError || 'Certificate is required to deploy branding or policies.')
        setShowCertificateDialog(true)
        return false
      }

      return true
    } catch (err: any) {
      setCertificateValid(false)
      setError(err.response?.data?.message || err.message || 'Failed to validate certificate configuration.')
      setShowCertificateDialog(true)
      return false
    } finally {
      setCheckingCertificate(false)
    }
  }

  const handleNext = async () => {
    // Step 0: Validate deployment options
    if (activeStep === 0) {
      // Calculate actual selections
      const isBrandingSelected = (deployBrandingCustomization || deployBrandingLogos)
      const isPoliciesSelected = enablePolicies && selectedScenario
      
      // Validate at least one option selected
      if (!isBrandingSelected && !isPoliciesSelected) {
        setError('Please select at least one deployment option (Branding or Policies)')
        return
      }
      
      // If policies enabled, ensure scenario selected
      if (enablePolicies && !selectedScenario) {
        setError('Please select a license scenario for policy deployment')
        return
      }

      // Ensure certificate exists even for branding-only deployments
      if (tenantId) {
        const certificateReady = await ensureCertificateConfigured()
        if (!certificateReady) {
          return
        }
      }
      
      // Determine deployment mode
      const brandingOnly = isBrandingSelected && !isPoliciesSelected

      if (isPoliciesSelected && !licenseAccess.canDeployPolicies) {
        setError(licenseAccess.portalLockReason || 'Policy deployment is locked until a valid Policy Deployment Wizard license is attached.')
        return
      }

      if (isBrandingSelected && !licenseAccess.canDeployBrandingOnly) {
        setError(licenseAccess.portalLockReason || 'Branding deployment is currently locked for this tenant.')
        return
      }

      setDeployingBrandingOnly(brandingOnly)
      
      // If branding-only, skip to Review step (step 2)
      if (brandingOnly) {
        if (tenantId) {
          await verifyTenantAccessBeforeProceeding(2)
        } else {
          setActiveStep(2)
        }
        return
      }
      
      // If deploying policies, verify tenant access
      if (tenantId) {
        await verifyTenantAccessBeforeProceeding()
      } else {
        setActiveStep((prevStep) => prevStep + 1)
      }
    } else {
      setActiveStep((prevStep) => prevStep + 1)
    }
  }

  const checkBrandingConfigured = async () => {
    if (!tenantId) return
    try {
      const branding = await getBranding(tenantId)
      const hasBranding = !!(branding.BackgroundColor || branding.HeaderBackgroundColor || branding.SignInPageText || branding.UsernameHintText || branding.CompanyLogoUrl)
      setHasBrandingConfig(hasBranding)
      
      // Check for branding icons/logos using the API
      try {
        const iconsResponse = await getBrandingIcons(tenantId)
        const iconCount = iconsResponse?.count ?? iconsResponse?.icons?.length ?? 0
        setBrandingIconCount(iconCount)
        const hasIcons = iconCount > 0
        setHasBrandingIcons(hasIcons)
        if (!hasIcons) {
          setDeployBrandingLogos(false)
        }
      } catch (iconErr) {
        console.log('No branding icons found')
        setBrandingIconCount(0)
        setHasBrandingIcons(false)
        setDeployBrandingLogos(false)
      }
      
      if (!hasBranding) {
        setDeployBrandingCustomization(false)
      }
    } catch (err) {
      // If branding not found or error, keep branding disabled by default
      setHasBrandingConfig(false)
      setHasBrandingIcons(false)
    }
  }

  const verifyTenantAccessBeforeProceeding = async (nextStepOverride?: number) => {
    if (!tenantId) return

    try {
      setVerifyingAccess(true)
      setError(null)

      console.log('🔍 Verifying tenant access for tenantId:', tenantId)
      const verification = await verifyTenantAccess(tenantId)
      console.log('🔍 Tenant access verification result:', verification)

      if (!verification.canAccessTenant) {
        setError(
          verification.errorMessage || 
          'Cannot connect to your Microsoft 365 tenant. Please verify your certificate configuration.'
        )
        return
      }

      if (!verification.hasRequiredPermissions) {
        const missingPerms = verification.missingPermissions?.join(', ') || 'unknown permissions'
        setError(
          `Missing required app permissions: ${missingPerms}. Please complete the Certificate Wizard and grant admin consent.`
        )
        return
      }

      // All checks passed, proceed to next step
      if (typeof nextStepOverride === 'number') {
        setActiveStep(nextStepOverride)
      } else {
        setActiveStep((prevStep) => prevStep + 1)
      }
      return true
    } catch (err: any) {
      console.error('Failed to verify tenant access:', err)
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to verify tenant access. Please check your certificate configuration.'
      )
      return false
    } finally {
      setVerifyingAccess(false)
    }
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleRecommendConfiguration = async () => {
    if (!tenantId) return

    if (!licenseAccess.canDeployPolicies) {
      setError(licenseAccess.portalLockReason || 'Policy deployment is locked until a valid Policy Deployment Wizard license is attached.')
      return
    }

    try {
      setIsRecommending(true)
      setError(null)

      // Check if certificate is configured
      const certStatus = await getCertificateStatus(tenantId)
      
    if (!certStatus.hasValidCertificate) {
      // Certificate exists but isn't functional - show specific error
      if (certStatus.hasCertificate && !certStatus.certificateFunctional) {
        setError(certStatus.certificateError || 'Certificate authentication failed...')
        setIsRecommending(false)
        return
      }

      // No certificate at all - show certificate wizard
      setShowCertificateDialog(true)
      setIsRecommending(false)
      return
   }

      // Get recommended scenarios from API
      const scenarios = await getRecommendedScenarios(tenantId)
      setRecommendedScenarios(scenarios)
      
      // Set the recommended scenario as selected by default
      setSelectedScenario(scenarios.recommended)

      // If recommended scenario is in advanced, show advanced section
      const isAdvanced = advancedLicenseScenarios.some(s => s.id === scenarios.recommended)
      if (isAdvanced) {
        setShowAdvanced(true)
      }
      
      setIsRecommending(false)
    } catch (err: any) {
      console.error('Failed to get recommendation:', err)
      
      if (err.response?.status === 400 && err.response?.data?.error === 'Certificate not configured') {
        setShowCertificateDialog(true)
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to analyze tenant licenses')
      }
      
      setIsRecommending(false)
    }
  }

  const handleCertificateDialogClose = () => {
    setShowCertificateDialog(false)
  }

  const handleGoToCertificateWizard = async () => {
    if (!tenantId) return
    
    setShowCertificateDialog(false)
    
    // Load customer data to get tenant ID (may be empty for first-time setup)
    try {
      const customer = await getCustomer(tenantId)
      setCertificateStatus({
        tenantId: customer.TenantID || '',
        tenantDomain: customer.CompanyName || 'your-tenant'
      })
      setShowCertificateWizard(true)
    } catch (err: any) {
      setError('Failed to load customer data: ' + err.message)
    }
  }

  const handleCertificateComplete = () => {
    setShowCertificateWizard(false)
    setCertificateStatus(null)
  }

  const handleVerifyGroups = async () => {
    if (!tenantId) return
    const selectedDeviceTypes = getSelectedDeviceTypes()
    if (selectedDeviceTypes.length === 0) {
      setError('Select at least one device type before verifying groups')
      return
    }
    
    try {
      setVerifyingGroups(true)
      setError(null)
      
      const result = await verifyRequiredGroups(tenantId, selectedDeviceTypes)
      setGroupsStatus({
        allExist: result.allGroupsExist,
        missingCount: result.missingCount,
        missingGroups: result.missingGroups
      })
      setGroupsVerified(true)
      
      if (result.allGroupsExist) {
        // Automatically proceed if all groups exist
        setTimeout(() => handleNext(), 1500)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify groups')
    } finally {
      setVerifyingGroups(false)
    }
  }

  const handleCreateGroups = async () => {
    if (!tenantId) return
    const selectedDeviceTypes = getSelectedDeviceTypes()
    if (selectedDeviceTypes.length === 0) {
      setError('Select at least one device type before creating groups')
      return
    }

    if (!licenseAccess.canCreateGroups) {
      setError(licenseAccess.portalLockReason || 'Group creation is locked until a valid Policy Deployment Wizard license is attached.')
      return
    }

    try {
      setCreatingGroups(true)
      setError(null)

      // Get owner email from subscription info
      const subscription = await getSubscriptionInfo(tenantId)
      const ownerEmail = subscription.CompanyName
        ? `admin@${subscription.TenantID}`
        : `admin@${subscription.TenantID}`

      // Start Pass 1 - License Groups Creation
      const pass1Result = await createLicenseGroupsPass1Async(tenantId, ownerEmail, selectedDeviceTypes)
      
      // Store session ID and show progress modal
      setGroupCreationSessionId(pass1Result.sessionId)
      setShowGroupProgressModal(true)
    } catch (err: any) {
      setError(err.message || 'Failed to start group creation')
      setCreatingGroups(false)
    }
  }

  const handleGroupCreationComplete = (success: boolean) => {
    setShowGroupProgressModal(false)
    setGroupCreationSessionId(null)
    setCreatingGroups(false)

    if (success) {
      setGroupsVerified(true)
      setGroupsStatus({
        allExist: true,
        missingCount: 0,
        missingGroups: []
      })
      // Auto-advance to next step after a brief pause
      setTimeout(() => handleNext(), 2000)
    }
  }

  const handleGroupCreationError = (errorMessage: string) => {
    setError(errorMessage)
    // Don't close the modal on error - let user retry
  }

  const getSelectedDeviceTypes = () =>
    Object.entries(deviceTypeSelections)
      .filter(([, enabled]) => enabled)
      .map(([deviceType]) => deviceType)

  const handleDeploy = async () => {
    if (deploying) return; // Prevent multiple deployments

    const isBrandingSelected = deployBrandingCustomization || deployBrandingLogos
    const isPoliciesSelected = enablePolicies && !!selectedScenario

    if (isPoliciesSelected && !licenseAccess.canDeployPolicies) {
      setError(licenseAccess.portalLockReason || 'Policy deployment is locked until a Policy Deployment Wizard license is attached.')
      return
    }

    if (isBrandingSelected && !licenseAccess.canDeployBrandingOnly) {
      setError(licenseAccess.portalLockReason || 'Branding deployment is currently locked for this tenant.')
      return
    }

    if (!isBrandingSelected && !isPoliciesSelected) {
      setError('Please select at least one deployment option (Branding or Policies)')
      return
    }

    if (enablePolicies && !selectedScenario) {
      setError('Please select a license scenario for policy deployment')
      return
    }

    const selectedDeviceTypes = getSelectedDeviceTypes()
    if (enablePolicies && selectedDeviceTypes.length === 0) {
      setError('Please select at least one device type for policy deployment')
      return
    }

    if (!tenantId) {
      setError('Customer ID is required');
      return;
    }

    try {
      setDeploying(true)
      setError(null)

      // Find the selected scenario to get its estimated policy count
      const allScenarios = [...standardLicenseScenarios, ...advancedLicenseScenarios]
      const selectedScenarioInfo = enablePolicies
        ? allScenarios.find(s => s.id === selectedScenario)
        : undefined

      // Send the selected scenario ID directly
      const request: DeploymentRequest = {
        TenantID: tenantId,
        SelectedDeviceTypes: selectedDeviceTypes,
        LicenseScenario: enablePolicies ? selectedScenario || undefined : undefined, // Optional for branding-only
        ApplyBranding: isBrandingSelected ? true : undefined, // Backward compat
        DeployBrandingCustomization: deployBrandingCustomization,
        DeployBrandingLogos: deployBrandingLogos,
        MSPTenantID: mspTenantId || undefined,
        EstimatedPolicies: enablePolicies ? (selectedScenarioInfo?.policies || 387) : undefined, // Send scenario estimate when deploying policies
        RetryFailedDeploymentId: retryingDeploymentId || undefined
      }

      const result = await startDeployment(request)
      console.log('🔍 Deployment API response:', result)
      console.log('🔍 result.success:', result.success)
      console.log('🔍 result.deploymentID:', result.deploymentID)
      setDeploymentResult(result)

      if (result.success && result.deploymentID) {
        // Fetch initial deployment status immediately before advancing
        try {
          const initialStatus = await getDeploymentStatus(result.deploymentID)
          setDeploymentStatus(initialStatus)
        } catch (err) {
          console.error('Error fetching initial deployment status:', err)
        }

        // Start polling for deployment status
        const interval = setInterval(async () => {
          try {
            const status = await getDeploymentStatus(result.deploymentID!)
            setDeploymentStatus(status)

            // Stop polling if deployment is complete - don't auto-advance, let user click Next
            if (status.status === 'Completed' || status.status === 'Failed' || status.status === 'PartialSuccess') {
              clearInterval(interval)
              setPollingInterval(null)
              setDeploying(false)
              // Don't call handleNext() here - user should click the button
            }
          } catch (err) {
            console.error('Error polling deployment status:', err)
          }
        }, 3000) // Poll every 3 seconds

        setPollingInterval(interval)
        handleNext() // Move to monitoring step
      } else {
        setDeploying(false)
        setError(result.errors?.join(', ') || 'Deployment failed')
      }
    } catch (err: any) {
      setDeploying(false)
      setError(err.message || 'Failed to start deployment')
    }
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Plan Your Deployment</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose what to deploy and confirm readiness before moving forward.
            </Typography>

            {/* Readiness Checks Card - First Position */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">Readiness Checks</Typography>
                  <Chip
                    label={certificateValid ? 'Certificate ready' : 'Certificate required'}
                    color={certificateValid ? 'success' : 'warning'}
                    icon={certificateValid ? <CheckCircle /> : <WarningIcon />}
                    variant={certificateValid ? 'filled' : 'outlined'}
                    size="small"
                  />
                </Box>

                {!certificateValid && !checkingCertificate && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Certificate required</AlertTitle>
                    Configure your certificate to enable license analysis, branding deployment, and policy deployment.
                  </Alert>
                )}

                {checkingCertificate && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CircularProgress size={20} />
                    <Typography>Checking certificate configuration...</Typography>
                  </Box>
                )}

                {certificateValid && licenseAccess.canDeployPolicies && !recommendedScenarios && !isRecommending && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CircularProgress size={20} />
                    <Typography>Analyzing your tenant licenses...</Typography>
                  </Box>
                )}

                {isRecommending && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CircularProgress size={20} />
                    <Typography>Analyzing license compatibility...</Typography>
                  </Box>
                )}

                {recommendedScenarios && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <AlertTitle>License analysis complete</AlertTitle>
                    We identified recommended policy sets for your current licensing.
                  </Alert>
                )}

                {!licenseAccess.canDeployPolicies && licenseAccess.requiresEnterpriseClientLicense && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Policy deployment locked</AlertTitle>
                    {licenseAccess.portalLockReason || 'Your MSP must attach a valid Policy Deployment Wizard license before policy deployment and group creation are enabled.'}
                  </Alert>
                )}

                {certificateValid && (
                  <Button
                    variant="outlined"
                    startIcon={<RecommendIcon />}
                    onClick={handleRecommendConfiguration}
                    disabled={isRecommending || checkingCertificate || !licenseAccess.canDeployPolicies}
                    size="small"
                  >
                    {isRecommending ? 'Analyzing...' : 'Refresh license analysis'}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Security Configuration Card - Second Position */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Configuration
                </Typography>

                {!licenseAccess.canDeployPolicies ? (
                  <Accordion elevation={0} expanded={false} disabled sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          Security Configuration (Locked)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {licenseAccess.portalLockReason || 'Security configuration is gated until a valid Policy Deployment Wizard license is attached.'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                  </Accordion>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {enablePolicies
                        ? 'Choose the configuration that matches your licensing'
                        : 'Policies are disabled'}
                    </Typography>

                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={selectedScenario}
                        onChange={(e) => setSelectedScenario(e.target.value)}
                      >
                        <Grid container spacing={2}>
                      {recommendedScenarios && (
                        <Grid item xs={12} key="recommended">
                          <Card
                            variant="outlined"
                            sx={{
                              cursor: enablePolicies ? 'pointer' : 'not-allowed',
                              opacity: enablePolicies ? 1 : 0.5,
                              border: selectedScenario === recommendedScenarios.recommended ? 3 : 2,
                              borderColor: selectedScenario === recommendedScenarios.recommended ? 'success.main' : 'primary.main',
                              bgcolor: headerBackgroundColor,
                              color: '#fff',
                              '&:hover': { borderColor: 'success.main' }
                            }}
                            onClick={() => enablePolicies && setSelectedScenario(recommendedScenarios.recommended)}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="h6" sx={{ color: '#fff' }} fontWeight="bold">
                                  ⭐ Recommended: {recommendedScenarios.recommended}
                                </Typography>
                                <FormControlLabel
                                  value={recommendedScenarios.recommended}
                                  control={<Radio />}
                                  label=""
                                  sx={{ m: 0 }}
                                  disabled={!enablePolicies}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                Best match for your current licensing.
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}

                      {/* Heading for alternate configurations */}
                      {recommendedScenarios &&
                        recommendedScenarios.viableScenarios.filter(
                          (s) => s !== recommendedScenarios.recommended
                        ).length > 0 && (
                          <Grid item xs={12}>
                            <Accordion elevation={0} defaultExpanded={false} sx={{ border: '1px solid', borderColor: 'divider', mt: 2 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  Select an alternate security configuration
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  {recommendedScenarios.viableScenarios
                                    .filter((s) => s !== recommendedScenarios.recommended)
                                    .map((scenarioId) => {
                                      const scenario = [
                                        ...standardLicenseScenarios,
                                        ...advancedLicenseScenarios,
                                      ].find((s) => s.id === scenarioId);
                                      if (!scenario) return null;

                                      return (
                                        <Grid item xs={12} md={6} key={scenario.id}>
                                          <Card
                                            variant="outlined"
                                            sx={{
                                              cursor: enablePolicies ? 'pointer' : 'not-allowed',
                                              opacity: enablePolicies ? 1 : 0.5,
                                              border: selectedScenario === scenario.id ? 2 : 1,
                                              borderColor:
                                                selectedScenario === scenario.id
                                                  ? 'primary.main'
                                                  : 'divider',
                                              '&:hover': { borderColor: 'primary.main' },
                                            }}
                                            onClick={() =>
                                              enablePolicies && setSelectedScenario(scenario.id)
                                            }
                                          >
                                            <CardContent>
                                              <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                              >
                                                <FormControlLabel
                                                  value={scenario.id}
                                                  control={<Radio />}
                                                  label=""
                                                  sx={{ m: 0 }}
                                                  disabled={!enablePolicies}
                                                />
                                                <Typography variant="h6">{scenario.name}</Typography>
                                              </Box>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 1 }}
                                              >
                                                {scenario.description}
                                              </Typography>
                                              <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={{ mt: 2 }}
                                              >
                                                <Typography variant="body2" color="text.secondary">
                                                  {scenario.policies} policies
                                                </Typography>
                                              </Box>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                sx={{ mt: 0.5 }}
                                              >
                                                Count may vary based on your tenant.
                                              </Typography>
                                            </CardContent>
                                          </Card>
                                        </Grid>
                                      );
                                    })}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        )}

                          {!recommendedScenarios && (
                        <>
                          {standardLicenseScenarios.map((scenario) => (
                            <Grid item xs={12} md={6} key={scenario.id}>
                              <Card
                                variant="outlined"
                                sx={{
                                  cursor: enablePolicies ? 'pointer' : 'not-allowed',
                                  opacity: enablePolicies ? 1 : 0.5,
                                  border: selectedScenario === scenario.id ? 2 : 1,
                                  borderColor: selectedScenario === scenario.id ? 'primary.main' : 'divider',
                                  '&:hover': { borderColor: 'primary.main' }
                                }}
                                onClick={() => enablePolicies && setSelectedScenario(scenario.id)}
                              >
                                <CardContent>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <FormControlLabel
                                      value={scenario.id}
                                      control={<Radio />}
                                      label=""
                                      sx={{ m: 0 }}
                                      disabled={!enablePolicies}
                                    />
                                    <Typography variant="h6">{scenario.name}</Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                                    {scenario.description}
                                  </Typography>
                                  <Box display="flex" gap={1}>
                                    <Chip
                                      label={`${scenario.policies} policies`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Count may vary based on your tenant.
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}

                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => setShowAdvanced(!showAdvanced)}
                              endIcon={<ExpandMoreIcon sx={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0)', transition: '0.3s' }} />}
                              sx={{ mt: 2 }}
                            >
                              {showAdvanced ? 'Hide' : 'Show'} Advanced Configurations
                            </Button>
                          </Grid>

                          {showAdvanced && advancedLicenseScenarios.map((scenario) => (
                            <Grid item xs={12} md={6} key={scenario.id}>
                              <Card
                                variant="outlined"
                                sx={{
                                  cursor: enablePolicies ? 'pointer' : 'not-allowed',
                                  opacity: enablePolicies ? 1 : 0.5,
                                  border: selectedScenario === scenario.id ? 2 : 1,
                                  borderColor: selectedScenario === scenario.id ? 'primary.main' : 'divider',
                                  '&:hover': { borderColor: 'primary.main' }
                                }}
                                onClick={() => enablePolicies && setSelectedScenario(scenario.id)}
                              >
                                <CardContent>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <FormControlLabel
                                      value={scenario.id}
                                      control={<Radio />}
                                      label=""
                                      sx={{ m: 0 }}
                                      disabled={!enablePolicies}
                                    />
                                    <Chip label="Advanced" color="secondary" size="small" />
                                  </Box>
                                  <Typography variant="h6" gutterBottom>
                                    {scenario.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" paragraph>
                                    {scenario.description}
                                  </Typography>
                                  <Box display="flex" gap={1}>
                                    <Chip
                                      label={`${scenario.policies} policies`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Count may vary based on your tenant.
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </>
                          )}
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Deployment Focus Card - Second Position */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Deployment Focus
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose what to deploy to your Microsoft 365 tenant
                </Typography>

                {/* Branding Configuration - Always visible, no master toggle */}
                <Paper elevation={0} sx={{ p: 2, mb: 2 }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                    <PaletteIcon color="primary" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Branding Configuration
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Customize your organization's sign-in experience
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ ml: 5, borderLeft: '2px solid', borderColor: 'divider', pl: 2 }}>
                    {/* Branding Customization Toggle */}
                    <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={deployBrandingCustomization}
                            onChange={(e) => setDeployBrandingCustomization(e.target.checked)}
                            disabled={!hasBrandingConfig}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              Branding Customization
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sign-in page colors, logo, and text
                            </Typography>
                            {!hasBrandingConfig && (
                              <Chip label="Not configured" size="small" color="warning" sx={{ ml: 1, mt: 0.5 }} />
                            )}
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 2 }}
                      />
                    </Paper>
                    {/* Branding Logos/Icons Toggle - match style */}
                    <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={deployBrandingLogos}
                            onChange={(e) => setDeployBrandingLogos(e.target.checked)}
                            disabled={!hasBrandingIcons}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              Branding Logos/Icons {brandingIconCount ? `(${brandingIconCount})` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Organizational icons and visual assets
                            </Typography>
                            {!hasBrandingIcons && (
                              <Chip label="No icons uploaded" size="small" color="warning" sx={{ ml: 1, mt: 0.5 }} />
                            )}
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                    </Paper>
                  </Box>
                </Paper>

                {/* Master Toggle: Policies */}
                {/* Security Policies Toggle - match style */}
                <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enablePolicies}
                        disabled={!licenseAccess.canDeployPolicies}
                        onChange={(e) => {
                          if (!licenseAccess.canDeployPolicies) {
                            return
                          }
                          setEnablePolicies(e.target.checked)
                          if (!e.target.checked) {
                            setSelectedScenario('')
                            setDeviceTypeSelections({
                              Android: false,
                              iPhone: false,
                              iPad: false,
                              macOS: false,
                              Linux: false,
                              Windows: false
                            })
                          } else if (recommendedScenarios?.recommended) {
                            setSelectedScenario(recommendedScenarios.recommended)
                            setDeviceTypeSelections({
                              Android: true,
                              iPhone: true,
                              iPad: true,
                              macOS: true,
                              Linux: true,
                              Windows: true
                            })
                          } else {
                            setDeviceTypeSelections((prev) => {
                              if (Object.values(prev).some(Boolean)) {
                                return prev
                              }

                              return {
                                Android: true,
                                iPhone: true,
                                iPad: true,
                                macOS: true,
                                Linux: true,
                                Windows: true
                              }
                            })
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <SecurityIcon color={enablePolicies ? 'primary' : 'disabled'} />
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              Security Policies
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Deploy recommended security configuration
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start' }}
                  />
                </Paper>

                {!licenseAccess.canDeployPolicies ? (
                  <Accordion elevation={0} expanded={false} disabled sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          Device Type Scope (Locked)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Device-type policy scope is unavailable until a Policy Deployment Wizard license is attached.
                        </Typography>
                      </Box>
                    </AccordionSummary>
                  </Accordion>
                ) : (
                  <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                      Device Type Scope
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select which endpoint-specific policy sets to include.
                    </Typography>

                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                      {deviceTypeOptions.map((option) => (
                        <FormControlLabel
                          key={option.key}
                          control={
                            <Switch
                              checked={deviceTypeSelections[option.key]}
                              disabled={!enablePolicies}
                              onChange={(e) => {
                                const nextChecked = e.target.checked
                                setDeviceTypeSelections((prev) => ({
                                  ...prev,
                                  [option.key]: nextChecked
                                }))
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {option.key}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start' }}
                        />
                      ))}
                    </Box>

                    {!enablePolicies && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Enable Security Policies to customize device-type scope.
                      </Alert>
                    )}
                  </Paper>
                )}

                {/* Validation Alert */}
                {(() => {
                  const isBrandingSelected = (deployBrandingCustomization || deployBrandingLogos)
                  const isPoliciesSelected = enablePolicies && selectedScenario
                  return !isBrandingSelected && !isPoliciesSelected && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please enable at least one branding or security deployment option to continue.
                    </Alert>
                  )
                })()}

                {/* Branding-only success message */}
                {(deployBrandingCustomization || deployBrandingLogos) && !enablePolicies && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <AlertTitle>Branding-only deployment</AlertTitle>
                    Only branding will be deployed. No security policies will change.
                  </Alert>
                )}

                {!licenseAccess.canDeployPolicies && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Security policy deployment is disabled until a Policy Deployment Wizard license is attached.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Quick Summary Card - Fourth Position with Next Button */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Summary
                </Typography>
                
                {/* Certificate status - First item */}
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  {certificateValid ? (
                    <>
                      <SuccessIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main">
                        Certificate configured and ready
                      </Typography>
                    </>
                  ) : (
                    <>
                      <WarningIcon color="warning" fontSize="small" />
                      <Typography variant="body2" color="warning.main">
                        Certificate configuration required
                      </Typography>
                    </>
                  )}
                </Box>
                
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
                  {/* Branding chips */}
                  {deployBrandingCustomization && (
                    <Chip label="Branding Customization" color="primary" size="small" icon={<CheckCircle />} />
                  )}
                  {deployBrandingLogos && (
                    <Chip label="Branding Icons" color="primary" size="small" icon={<CheckCircle />} />
                  )}
                  
                  {/* Policies chip */}
                  {enablePolicies && selectedScenario && (
                    <Chip label={`Policies: ${selectedScenario}`} color="primary" size="small" icon={<CheckCircle />} />
                  )}
                  {enablePolicies && selectedScenario && (
                    <Chip label={`Devices: ${getSelectedDeviceTypes().join(', ') || 'None selected'}`} color="default" size="small" />
                  )}
                  
                  {(() => {
                    const isBrandingSelected = (deployBrandingCustomization || deployBrandingLogos)
                    const isPoliciesSelected = enablePolicies && selectedScenario
                    return !isBrandingSelected && !isPoliciesSelected && (
                      <Chip label="Nothing selected" color="default" size="small" />
                    )
                  })()}
                </Box>
                
                {/* Next button to advance to next step in wizard */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleNext}
                  disabled={(() => {
                    const isBrandingSelected = (deployBrandingCustomization || deployBrandingLogos)
                    const isPoliciesSelected = enablePolicies && selectedScenario
                    return !isBrandingSelected && !isPoliciesSelected || !certificateValid || checkingCertificate
                  })()}
                  startIcon={verifyingAccess ? <CircularProgress size={20} /> : null}
                >
                  {verifyingAccess ? 'Verifying Tenant Access...' : 'Next'}
                </Button>
              </CardContent>
            </Card>
          </Box>
        )

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Setup Required Groups
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Azure AD groups are required for policy assignment. We'll verify and create the necessary groups in your tenant.
            </Typography>

            {!groupsVerified && !verifyingGroups && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Required Groups
                </Typography>
                <Typography variant="body2">
                  This step will create approximately 40+ security groups needed for policy assignment, including:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Device security level groups (Basic, Enhanced, High)" /></ListItem>
                  <ListItem><ListItemText primary="• Platform-specific groups (iOS, Android, Windows, macOS)" /></ListItem>
                  <ListItem><ListItemText primary="• Ownership type groups (Corporate, BYOD)" /></ListItem>
                  <ListItem><ListItemText primary="• Administrative and licensing groups" /></ListItem>
                </List>
              </Alert>
            )}

            {verifyingGroups && (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Verifying existing groups...
                </Typography>
              </Box>
            )}

            {groupsVerified && (
              <>
                {groupsStatus.allExist ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      All Required Groups Exist
                    </Typography>
                    <Typography variant="body2">
                      All necessary groups are already configured in your tenant. You can proceed to deployment.
                    </Typography>
                  </Alert>
                ) : (
                  <>
                    {!licenseAccess.canCreateGroups && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          Group creation is locked
                        </Typography>
                        <Typography variant="body2">
                          {licenseAccess.portalLockReason || 'Your MSP must attach a valid Enterprise Client License before group creation is enabled.'}
                        </Typography>
                      </Alert>
                    )}
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Missing Groups: {groupsStatus.missingCount}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        We need to deploy some groups before we proceed with policy deployment.
                      </Typography>
                      {groupsStatus.missingGroups.length > 0 && groupsStatus.missingGroups.length <= 10 && (
                        <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                          {groupsStatus.missingGroups.map((group, idx) => (
                            <Typography key={idx} variant="caption" display="block">
                              • {group}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Alert>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleCreateGroups}
                      disabled={creatingGroups || !licenseAccess.canCreateGroups}
                      sx={{ mb: 2 }}
                    >
                      Create Missing Groups
                    </Button>
                  </>
                )}
              </>
            )}

            {!groupsVerified && !verifyingGroups && (
              <Button
                variant="contained"
                fullWidth
                onClick={handleVerifyGroups}
                disabled={!tenantId}
              >
                Verify Groups
              </Button>
            )}
          </Box>
        )

      case 2:
        const allScenarios = [...standardLicenseScenarios, ...advancedLicenseScenarios]
        const selectedScenarioInfo = allScenarios.find(s => s.id === selectedScenario)
        const isBrandingOnly = deployingBrandingOnly || ((deployBrandingCustomization || deployBrandingLogos) && !selectedScenario)
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Deployment
            </Typography>

            {/* MSP policy prefix info banner */}
            {mspTenantId && effectivePolicyPrefix !== undefined && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {effectivePolicyPrefix
                  ? <>Policies will be deployed with prefix: <strong>{effectivePolicyPrefix}-</strong> (e.g. <em>{effectivePolicyPrefix}-ConditionalAccess-Baseline</em>)</>
                  : 'No policy name prefix will be applied for this deployment.'}
              </Alert>
            )}

            {/* Branding-Only Deployment Alert */}
            {isBrandingOnly && (
              <Alert severity="warning" sx={{ mb: 3, backgroundColor: '#fff4ce', borderColor: '#FFB900' }}>
                <AlertTitle>Branding-Only Deployment</AlertTitle>
                You are deploying ONLY organizational branding. No security policies will be deployed or modified.
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(deployBrandingCustomization || deployBrandingLogos) && (
                <Grid item xs={12} md={isBrandingOnly ? 12 : ((deployBrandingCustomization || deployBrandingLogos) && selectedScenario ? 4 : 6)}>
                  <Paper elevation={0} sx={{ p: 3, height: '100%' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PaletteIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
                      <Typography variant="h6" color="primary.dark">
                        Organizational Branding
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      Apply to Tenant
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sign-in page customization will be deployed
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {selectedScenario && (
                <>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, height: '100%' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        License Scenario
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedScenarioInfo?.name}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, height: '100%' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {deploymentResult?.totalPolicies
                          ? 'In-Scope Policies'
                          : 'Estimated Policies'}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {deploymentResult?.totalPolicies
                          ? deploymentResult.totalPolicies
                          : selectedScenarioInfo?.policies
                            ? `~${selectedScenarioInfo.policies}`
                            : 'Calculating...'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {deploymentResult?.totalPolicies
                          ? 'Policies in scope after device-type filtering.'
                          : 'Estimated counts may vary based on existing tenant policies.'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, height: '100%' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Device Scope
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {getSelectedDeviceTypes().join(', ') || 'None selected'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Multi-device and tenant-wide policies are always included.
                      </Typography>
                    </Paper>
                  </Grid>
                </>
              )}
            </Grid>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                What will happen:
              </Typography>
              <Typography variant="body2" component="div">
                {(deployBrandingCustomization || deployBrandingLogos) && (
                  <>• Organizational branding will be applied to your tenant sign-in page<br /></>
                )}
                {selectedScenario && (
                  <>
                    • Policies will be deployed to your tenant via Microsoft Graph API<br />
                    • Existing policies will be updated if they already exist<br />
                    • All policies will be assigned to appropriate groups<br />
                  </>
                )}
                • Full audit log will be created<br />
                • Estimated time: {isBrandingOnly ? '2-3' : '10-15'} minutes
              </Typography>
            </Alert>
          </Box>
        )

      case 3:
        const completionStatuses = ['Completed', 'Failed', 'PartialSuccess']
        const isDeploymentComplete = deploymentStatus && completionStatuses.includes(deploymentStatus.status)
        const progress = deploymentStatus
          ? isDeploymentComplete
            ? 100
            : deploymentStatus.totalPolicies > 0
              ? Math.min(100, ((deploymentStatus.successfulPolicies + deploymentStatus.failedPolicies + deploymentStatus.skippedPolicies) / deploymentStatus.totalPolicies) * 100)
              : 0
          : 0

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {isDeploymentComplete ? 'Deployment Complete' : 'Deployment in Progress'}
            </Typography>

            {deploymentStatus ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {!isDeploymentComplete && <CircularProgress size={24} sx={{ mr: 2 }} />}
                  {isDeploymentComplete ? (
                    deploymentStatus.status === 'Completed' ? (
                      <CheckCircle color="success" sx={{ mr: 2 }} />
                    ) : (
                      <ErrorIcon color="error" sx={{ mr: 2 }} />
                    )
                  ) : null}
                  <Typography>
                    {isDeploymentComplete 
                      ? `Deployment ${deploymentStatus.status.toLowerCase()}`
                      : 'Deploying policies to your tenant...'}
                  </Typography>
                </Box>

                <LinearProgress 
                  variant="determinate" 
                  value={Number.isFinite(progress) ? progress : 0} 
                  sx={{ mb: 2, height: 10, borderRadius: 5 }}
                  color={isDeploymentComplete ? (deploymentStatus.status === 'Completed' ? 'success' : 'error') : 'primary'}
                />

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }} style={{ backgroundColor: successBoxStyles.backgroundColor, border: successBoxStyles.border }}>
                      <Typography variant="h4" color="success.main">
                        {deploymentStatus.successfulPolicies}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successful
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center', cursor: 'pointer' }} onClick={() => setFailedDialogOpen(true)}>
                      <Typography variant="h4" color="error.main">
                        {deploymentStatus.failedPolicies}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Failed
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }} onClick={() => setSkippedDialogOpen(true)}>
                      <Typography variant="h4" color="warning.main">
                        {deploymentStatus.skippedPolicies}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Skipped
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                      <Typography variant="h4">
                        {deploymentStatus.totalPolicies}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Alert 
                  severity={isDeploymentComplete ? (deploymentStatus.status === 'Completed' ? 'success' : 'error') : 'info'} 
                  sx={{ mt: 2 }}
                >
                  Status: {deploymentStatus.status}
                  {isDeploymentComplete && ' - Click VIEW RESULTS to see detailed results'}
                </Alert>
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography>Starting deployment...</Typography>
                </Box>
                <Alert severity="info">
                  Deployment initiated. Waiting for status updates...
                </Alert>
              </Box>
            )}
          </Box>
        )

      case 4:
        // Use final deployment status from polling, not the initial result
        const finalStatus = deploymentStatus || deploymentResult
        const finalStatusValue = finalStatus?.status
        const isSuccess = finalStatus && (finalStatusValue === 'Completed' || (deploymentResult?.success && deploymentResult.status === 'Completed'))

        // Calculate duration from timestamps if available (deploymentStatus has startDate/endDate)
        const durationFromStatus = deploymentStatus?.startDate && deploymentStatus?.endDate
          ? (() => {
              const start = new Date(deploymentStatus.startDate)
              const end = new Date(deploymentStatus.endDate)
              const diff = end.getTime() - start.getTime()
              if (isNaN(diff) || diff < 0) return null
              const totalSeconds = Math.floor(diff / 1000)
              const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
              const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
              const s = String(totalSeconds % 60).padStart(2, '0')
              return `${h}:${m}:${s}`
            })()
          : null
        const durationText = durationFromStatus || deploymentResult?.duration || 'N/A'
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Deployment Complete
            </Typography>

            {finalStatus && (
              <>
                {isSuccess ? (
                  <Alert severity="success" icon={<SuccessIcon />} sx={{ mb: 3 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Deployment completed successfully!
                    </Typography>
                    {durationText && durationText !== 'N/A' && (
                      <Typography variant="body2">
                        Duration: {durationText}
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Deployment completed with errors
                    </Typography>
                    <Typography variant="body2">
                      Status: {finalStatusValue || 'Unknown'}
                    </Typography>
                  </Alert>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                      <SuccessIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {finalStatus.successfulPolicies || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successful
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      elevation={2}
                      role="button"
                      onClick={() => setFailedDialogOpen(true)}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        opacity: 1
                      }}
                    >
                      <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {finalStatus.failedPolicies || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Failed
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      elevation={2}
                      role="button"
                      onClick={() => setSkippedDialogOpen(true)}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        opacity: 1
                      }}
                    >
                      <WarningIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {finalStatus.skippedPolicies || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Skipped
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                      <InfoIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {finalStatus.totalPolicies || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {deploymentResult?.errors && deploymentResult.errors.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom color="error">
                      Errors ({deploymentResult.errors.length})
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, maxHeight: 200, overflow: 'auto' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                      <List dense>
                        {deploymentResult.errors.map((error: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <ErrorIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={error}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </>
                )}

                {deploymentResult?.skippedPoliciesDetails && deploymentResult.skippedPoliciesDetails.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" gutterBottom color="warning.main">
                        Not all policies implemented ({deploymentResult.skippedPoliciesDetails.length})
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowSkippedDetails(!showSkippedDetails)}
                        startIcon={showSkippedDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        {showSkippedDetails ? 'Hide Details' : 'More Information'}
                      </Button>
                    </Box>
                    {showSkippedDetails && (
                      <Paper elevation={0} sx={{ p: 2, maxHeight: 300, overflow: 'auto' }} style={{ backgroundColor: boxStyles.backgroundColor, border: boxStyles.border }}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          The following policies were skipped due to licensing or service plan requirements. Click the guidance links to learn how to enable these features.
                        </Typography>
                        <List dense>
                          {deploymentResult.skippedPoliciesDetails.map((skipped, index) => (
                            <ListItem key={index} sx={{ alignItems: 'flex-start' }}>
                              <ListItemIcon>
                                <WarningIcon color="warning" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight="medium">
                                    {skipped.PolicyName}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      {skipped.Reason}
                                    </Typography>
                                    {skipped.GuidanceLink && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        href={skipped.GuidanceLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ mt: 1, p: 0, minHeight: 'auto', fontSize: '0.75rem' }}
                                      >
                                        Learn More
                                      </Button>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </>
                )}

                <Dialog open={skippedDialogOpen} onClose={() => setSkippedDialogOpen(false)} maxWidth="md" fullWidth>
                  <DialogTitle>Skipped Policies ({deploymentStatus?.skippedPolicies || finalStatus?.skippedPolicies || 0})</DialogTitle>
                  <DialogContent dividers>
                    {deploymentResult?.skippedPoliciesDetails && deploymentResult.skippedPoliciesDetails.length > 0 ? (
                      <List dense>
                        {deploymentResult.skippedPoliciesDetails.map((skipped, index) => (
                          <ListItem key={index} alignItems="flex-start">
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary={skipped.PolicyName}
                              secondary={
                                <Typography variant="body2" color="text.secondary">
                                  {skipped.Reason || 'Skipped during deployment'}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (deploymentStatus?.skippedPolicies || finalStatus?.skippedPolicies) > 0 ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {(deploymentStatus?.skippedPolicies || finalStatus?.skippedPolicies)} policies were skipped due to licensing or service plan requirements.
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        No skipped policies were reported for this deployment.
                      </Alert>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSkippedDialogOpen(false)}>Close</Button>
                  </DialogActions>
                </Dialog>

                <Dialog open={failedDialogOpen} onClose={() => setFailedDialogOpen(false)} maxWidth="md" fullWidth>
                  <DialogTitle>Failed Items</DialogTitle>
                  <DialogContent dividers>
                    {(deploymentResult?.errors && deploymentResult.errors.length > 0) || (deploymentStatus?.nonPolicyErrors && deploymentStatus.nonPolicyErrors.length > 0) || deploymentStatus?.errorMessage ? (
                      <List dense>
                        {deploymentResult?.errors && deploymentResult.errors.map((error, index) => (
                          <ListItem key={`policy-${index}`}>
                            <ListItemIcon>
                              <ErrorIcon color="error" />
                            </ListItemIcon>
                            <ListItemText primary={error} />
                          </ListItem>
                        ))}
                        {deploymentStatus?.nonPolicyErrors && deploymentStatus.nonPolicyErrors
                          .filter((err) => !(deploymentStatus.brandingLogos && err.category === 'Branding Logos'))
                          .map((err, index) => (
                          <ListItem key={`nonpro-${index}`}>
                            <ListItemIcon>
                              <ErrorIcon color="error" />
                            </ListItemIcon>
                            <ListItemText primary={`${err.category}: ${err.message}`} />
                          </ListItem>
                        ))}
                        <BrandingAssetStatusPanel brandingLogos={deploymentStatus?.brandingLogos} />
                        {!deploymentStatus?.nonPolicyErrors?.length && !deploymentStatus?.brandingLogos && deploymentStatus?.errorMessage && (
                          <ListItem>
                            <ListItemIcon>
                              <ErrorIcon color="error" />
                            </ListItemIcon>
                            <ListItemText primary={deploymentStatus.errorMessage} />
                          </ListItem>
                        )}
                      </List>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        No detailed failure messages are available.
                      </Alert>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setFailedDialogOpen(false)}>Close</Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Box>
        )

      default:
        return 'Unknown step'
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Deploy Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Deploy security policies to your Microsoft 365 tenant
      </Typography>

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ minHeight: 400 }}>
            {getStepContent(activeStep)}
          </Box>

          {/* Certificate Warning at Bottom - only show on step 0 */}
          {activeStep === 0 && !checkingCertificate && !certificateValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Certificate Required</AlertTitle>
              <Typography variant="body2" paragraph>
                You must complete the Certificate Wizard before you can deploy branding or policies.
                The certificate allows this application to securely connect to your Microsoft 365 tenant.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  if (!tenantId) return
                  const customer = await getCustomer(tenantId)
                  setCertificateStatus({
                    tenantId: customer.TenantID || '',
                    tenantDomain: customer.CompanyName || 'your-tenant'
                  })
                  setShowCertificateWizard(true)
                }}
              >
                Start Certificate Wizard
              </Button>
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            {activeStep !== 4 && (
              <Button
                disabled={activeStep === 0 || deploying || creatingGroups || verifyingGroups}
                onClick={handleBack}
              >
                Back
              </Button>
            )}
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button onClick={() => { window.location.href = '/' }} variant="contained">
                  CLOSE WIZARD
                </Button>
              ) : activeStep === 1 ? (
                // Groups step - only show Next if groups are verified and exist
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!groupsVerified || !groupsStatus.allExist || creatingGroups}
                >
                  Next
                </Button>
              ) : activeStep === 2 ? (
                // Review step - show Deploy button
                <Button
                  variant="contained"
                  onClick={handleDeploy}
                  disabled={(!selectedScenario && !deployBrandingCustomization && !deployBrandingLogos) || deploying}
                  startIcon={<DeployIcon />}
                >
                  Start Deployment
                </Button>
              ) : activeStep === 3 ? (
                // Deployment Progress step - only enable Next when deployment is complete
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!deploymentStatus || !['Completed', 'Failed', 'PartialSuccess'].includes(deploymentStatus.status)}
                  startIcon={deploymentStatus && ['Completed', 'Failed', 'PartialSuccess'].includes(deploymentStatus.status) ? null : <CircularProgress size={20} />}
                >
                  {deploymentStatus && ['Completed', 'Failed', 'PartialSuccess'].includes(deploymentStatus.status)
                    ? 'View Results' 
                    : 'Deploying...'}
                </Button>
              ) : activeStep !== 0 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : null}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Certificate Dialog */}
      <Dialog open={showCertificateDialog} onClose={handleCertificateDialogClose}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Certificate Configuration Required</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            To recommend a configuration, the application needs to read your tenant's license information from Microsoft 365.
          </Typography>
          <Typography variant="body1" paragraph>
            Please complete the Certificate Wizard to configure authentication. This will allow the application to:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Detect your tenant's licensing (E5, E3, Business Premium, etc.)" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Recommend the optimal configuration for your environment" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Deploy policies to your Microsoft 365 tenant" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCertificateDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleGoToCertificateWizard}>
            Start Certificate Wizard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificate Wizard Dialog */}
      {showCertificateWizard && tenantId && certificateStatus && (
        <Dialog
          open={showCertificateWizard}
          onClose={() => setShowCertificateWizard(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Certificate Setup Wizard
            <IconButton
              onClick={() => setShowCertificateWizard(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <CertificateWizard
              tenantId={tenantId}
              tenantDomain={certificateStatus.tenantDomain || 'your-tenant'}
              onComplete={handleCertificateComplete}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Group Creation Progress Modal */}
      {tenantId && (
        <GroupCreationProgressModal
          open={showGroupProgressModal}
          sessionId={groupCreationSessionId}
          tenantId={tenantId}
          onComplete={handleGroupCreationComplete}
          onError={handleGroupCreationError}
        />
      )}
    </Box>
  )
}
